import { Chip, ChipPicker, ErrorChip, ListItem, TabStyles } from '@tableau/widgets';
import * as React from 'react';
import { Dictionary } from 'underscore';
import { CommonStyles } from '../../../common/react/CommonStyles';
import { cssClass } from '../../../style';
import { ConceptName } from '../../types/ConceptName';

/**
 * This is a wrapper tsx that makes the ChipPicker widget more suitable to the purposes of dataroles.
 */

export interface SynonymsChipPickerProps {
  readonly placeholder: string;
  readonly label: string;
  readonly testId: string;
  readonly values: ReadonlyArray<ConceptName>;
  readonly onChange: (newValues: ConceptName[]) => void;
  readonly style?: React.CSSProperties;
  readonly chipContentFormatter?: (value: string) => React.ReactNode;
  readonly getErrorMessage: (value: string) => string;
}

export interface SynonymsChipPickerState {
  readonly inputText: string;
}
const cssPrefix = 'SynonymChipEditor';
const labelElim = cssClass(cssPrefix, 'labelElimination', {
  $nest: {
    '& label': {
      display: 'none',
    },
  },
});

export class SynonymsChipPicker extends React.Component<SynonymsChipPickerProps, SynonymsChipPickerState> {
  public state: SynonymsChipPickerState = {
    inputText: '',
  };

  public render(): JSX.Element  {
    return <div className={labelElim} style={this.props.style}>
    <ChipPicker
      label={this.props.label}
      aria-label={this.props.label}
      placeholder={this.props.placeholder}
      testId={this.props.testId}
      listItems={this.currentResults}
      onValueAdd={this.handleValueAdd}
      onValuesChange={this.handleValuesChange}
      onFilterItems={this.handleFilterItems}
      style={this.props.style}>
      {this.props.values.map((value: ConceptName, i: number) => {
        const chipProps = {
          tooltip: value.name,
          value: value.name,
          children: this.props.chipContentFormatter ? this.props.chipContentFormatter(value.name) : value.name,
        };
        const errorMessage = this.props.getErrorMessage(value.name);
        if(errorMessage === '') { return <Chip {...chipProps}/>; }
        return <ErrorChip key={i} message={errorMessage} {...chipProps}/>;
      })}
    </ChipPicker>
    </div>;
  }

  public shouldComponentUpdate(nextProps: SynonymsChipPickerProps, nextState: SynonymsChipPickerState): boolean{
    return this.props.placeholder !== nextProps.placeholder ||
    this.props.placeholder !== nextProps.placeholder ||
    this.props.testId !== nextProps.testId ||
    this.props.values.some((v, i) => v !== nextProps.values[i]) ||
    Object.keys(this.props.style).some((key: keyof React.CSSProperties) => nextProps.style[key] !== this.props.style[key]) ||
    this.state.inputText !== nextState.inputText;
  }

  private get currentResults(): ListItem[] {
    const filterText = this.state.inputText.trim();
    if(filterText === '') {return [];}
    const errorMessage = this.props.getErrorMessage(filterText) ;
    if(errorMessage === '') {
      return [{value: filterText, disabled: !!this.props.values.find(value => value.name === filterText)}];
    }
    return [{
      value: filterText,
      content: <div style={{color: TabStyles.Colors.AttentionLight}} children={errorMessage}/>,
    }];
  }

  private handleFilterItems = (s: string) => this.setState({inputText: s});

  private handleValuesChange = (values: string[]) => {
    const valueNameMap: Dictionary<ConceptName> = {};
    this.props.values.forEach(value => valueNameMap[value.name] = value);
    const updatedValueList = values.map(value => valueNameMap[value]);
    this.props.onChange(updatedValueList);
  }

  private handleValueAdd = (value: string, id: number) => {
    if(this.props.getErrorMessage(value) !== '') { return; }
    const newValues = [...this.props.values];
    newValues.splice(id, 0, {
      name: value,
      locale: '',
      weight: 1,
      is_primary: false,
    });
    this.props.onChange(newValues);
  }
}
